import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, CardActions, Grid, Button,
  Chip, TextField, MenuItem, InputAdornment, CircularProgress,
  Alert, Snackbar, IconButton, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CalculateIcon from '@mui/icons-material/Calculate';
import calculationService from '../services/calculationService';
import FormulaBuilder from './FormulaBuilder';

const typeChipColor = (type) => {
  if (!type) return 'default';
  const t = type.toLowerCase();
  if (t.includes('fuel')) return 'warning';
  if (t.includes('distance')) return 'info';
  if (t.includes('spend')) return 'success';
  if (t.includes('market') || t.includes('location')) return 'secondary';
  if (t.includes('co2e') || t.includes('carbon')) return 'error';
  if (t.includes('custom')) return 'default';
  return 'primary';
};

export default function Calculations() {
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [formulaTypes, setFormulaTypes] = useState([]);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingFormula, setEditingFormula] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, formula: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadCalculations();
    loadTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCalculations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, typeFilter]);

  const loadCalculations = async () => {
    try {
      setLoading(true);
      const data = await calculationService.getAll({
        search: search || undefined,
        formula_type: typeFilter || undefined
      });
      setCalculations(data ?? []);
    } catch (err) {
      setError(err.message || 'Failed to load calculations');
    } finally {
      setLoading(false);
    }
  };

  const loadTypes = async () => {
    try {
      const types = await calculationService.getTypes();
      setFormulaTypes(types ?? []);
    } catch (_) { /* ignore */ }
  };

  const handleNewCalculation = () => {
    setEditingFormula(null);
    setBuilderOpen(true);
  };

  const handleEditFormula = (formula) => {
    setEditingFormula(formula);
    setBuilderOpen(true);
  };

  const handleSave = async (data) => {
    try {
      if (editingFormula) {
        await calculationService.update(editingFormula.emission_calculation_formula_id, data);
        setSnackbar({ open: true, message: 'Calculation updated', severity: 'success' });
      } else {
        await calculationService.create(data);
        setSnackbar({ open: true, message: 'Calculation created', severity: 'success' });
      }
      setBuilderOpen(false);
      setEditingFormula(null);
      loadCalculations();
    } catch (err) {
      const errData = err.response?.data?.error;
      const errMsg = typeof errData === 'string' ? errData : errData?.message || err.message || 'Save failed';
      setSnackbar({ open: true, message: errMsg, severity: 'error' });
    }
  };

  const handleDeleteConfirm = async () => {
    const formula = deleteDialog.formula;
    setDeleteDialog({ open: false, formula: null });
    try {
      await calculationService.delete(formula.emission_calculation_formula_id);
      setSnackbar({ open: true, message: 'Calculation deleted', severity: 'success' });
      loadCalculations();
    } catch (err) {
      const errData = err.response?.data?.error;
      const errMsg = typeof errData === 'string' ? errData : errData?.message || err.message || 'Delete failed';
      setSnackbar({ open: true, message: errMsg, severity: 'error' });
    }
  };

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalculateIcon color="primary" /> Calculation Formulas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage emission calculation formulas for the Open Footprint database
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewCalculation}>
          New Calculation
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search calculations..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
          }}
          sx={{ width: 300 }}
        />
        <TextField
          size="small"
          select
          label="Formula Type"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          sx={{ width: 200 }}
        >
          <MenuItem value="">All Types</MenuItem>
          {formulaTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 'auto' }}>
          {calculations.length} formula{calculations.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Loading */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : calculations.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <CalculateIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
          <Typography variant="h6">No calculations found</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {search || typeFilter ? 'Try adjusting your filters' : 'Create your first calculation formula'}
          </Typography>
          {!search && !typeFilter && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleNewCalculation}>
              New Calculation
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {calculations.map(calc => (
            <Grid item xs={12} sm={6} md={4} key={calc.emission_calculation_formula_id}>
              <Card variant="outlined" sx={{
                height: '100%', display: 'flex', flexDirection: 'column',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: 3 }
              }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {calc.name}
                    </Typography>
                    {calc.formula_type && (
                      <Chip label={calc.formula_type} size="small" color={typeChipColor(calc.formula_type)} />
                    )}
                  </Box>
                  {calc.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {calc.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Formula
                  </Typography>
                  <Box sx={{ mt: 0.5, p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="body2" sx={{
                      fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.primary',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                    }}>
                      {calc.formula_expression || <em style={{ color: '#999' }}>No expression defined</em>}
                    </Typography>
                  </Box>
                  {calc.expression_language && calc.expression_language !== 'Plain Text' && (
                    <Chip label={calc.expression_language} size="small" variant="outlined" sx={{ mt: 1, fontSize: '0.7rem' }} />
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 1.5 }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditFormula(calc)}>
                    Edit Formula
                  </Button>
                  <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, formula: calc })}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Formula Builder Dialog */}
      <FormulaBuilder
        open={builderOpen}
        onClose={() => { setBuilderOpen(false); setEditingFormula(null); }}
        onSave={handleSave}
        formula={editingFormula}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, formula: null })}>
        <DialogTitle>Delete Calculation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteDialog.formula?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, formula: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
